import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ProjectEditModal from './ProjectEditModal';
import UserMenu from './UserMenu';
import { useSystemSettings } from '../hooks/useSystemSettings';
import ToggleSwitch from './ToggleSwitch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  // Actors functionality removed
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  
  // New states for task and contact management
  const [newTask, setNewTask] = useState({ title: '', deadline: '' });
  const [newContact, setNewContact] = useState({ name: '', position: '', phone: '', email: '' });
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [managers, setManagers] = useState([]);
  const [assigningManager, setAssigningManager] = useState(false);
  const [comments, setComments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [newLink, setNewLink] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [showAddComment, setShowAddComment] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [links, setLinks] = useState([]);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const { getIndustryLabel, getStageLabel, taskTemplates } = useSystemSettings();

  const readinessLabels = {
    concept: 'Концепт',
    design: 'Проектирование',
    construction_start: 'Начало строительства',
    active_construction: 'Активная стройка',
    approaching_em: 'Подходит к ЭМ',
    completion: 'Завершение'
  };

  // Use dynamic stage labels from system settings

  useEffect(() => {
    if (id) {
      // Save referrer info for smart back navigation
      const savedReferrer = localStorage.getItem('projectDetailReferrer');
      if (savedReferrer) {
        setReferrerInfo(JSON.parse(savedReferrer));
      }
      
      loadProjectData();
      loadPhotos();
      loadLinks();
    }
  }, [id]);

  // ESC key handler for photo modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        closePhotoModal();
      }
    };
    
    if (isPhotoModalOpen) {
      document.addEventListener('keydown', handleEsc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isPhotoModalOpen]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectResponse = await axios.get(`${API}/projects/${id}`);
      const projectData = projectResponse.data;
      setProject(projectData);
      
      // Set tasks and contacts from project data
      setTasks(projectData.tasks || []);
      setContacts(projectData.contacts || []);

      // Load project photos
      const photosResponse = await axios.get(`${API}/projects/${id}/photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPhotos(photosResponse.data.photos || []);

      // Load project comments
      await loadComments();

      // Actors functionality removed

    } catch (err) {
      console.error('Failed to load project data:', err);
      setError('Не удалось загрузить данные проекта');
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPhotos(response.data.photos || []);
    } catch (err) {
      console.error('Failed to load photos:', err);
    }
  };

  const loadLinks = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinks(response.data.links || []);
    } catch (err) {
      console.error('Failed to load links:', err);
    }
  };

  const handleProjectUpdated = (updatedProject) => {
    setProject(updatedProject);
    setShowEditModal(false);
  };

  // Task management functions
  const addTask = async () => {
    if (!newTask.title.trim()) return;
    
    try {
      await axios.post(`${API}/projects/${id}/tasks`, newTask);
      setNewTask({ title: '', deadline: '' });
      loadProjectData(); // Reload to get updated tasks
    } catch (err) {
      console.error('Failed to add task:', err);
      alert('Не удалось добавить задачу: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const updateTask = async (taskId, taskData) => {
    try {
      await axios.put(`${API}/projects/${id}/tasks/${taskId}`, taskData);
      loadProjectData(); // Reload to get updated tasks
      setEditingTaskId(null);
    } catch (err) {
      console.error('Failed to update task:', err);
      alert('Не удалось обновить задачу: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Удалить эту задачу?')) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/tasks/${taskId}`);
      loadProjectData(); // Reload to get updated tasks
    } catch (err) {
      console.error('Failed to delete task:', err);
      alert('Не удалось удалить задачу: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  // Contact management functions
  const addContact = async () => {
    if (!newContact.name.trim()) return;
    
    try {
      let updatedContacts;
      
      if (editingContactIndex !== null) {
        // Update existing contact
        updatedContacts = [...contacts];
        updatedContacts[editingContactIndex] = newContact;
      } else {
        // Add new contact
        updatedContacts = [...contacts, newContact];
      }
      
      await axios.put(`${API}/projects/${id}`, {
        ...project,
        contacts: updatedContacts
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setContacts(updatedContacts);
      setNewContact({ name: '', position: '', phone: '', email: '' });
      setEditingContactIndex(null);
      setShowAddContact(false);
      
    } catch (err) {
      console.error('Failed to save contact:', err);
      alert('Не удалось сохранить контакт: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  // Photo management functions
  const convertDriveUrlToViewable = (driveUrl) => {
    if (!driveUrl) return '';
    
    // Извлекаем file ID из Google Drive URL
    const fileIdMatch = driveUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      // Используем публичный прямой доступ к изображению без требования аутентификации
      return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
    
    return driveUrl;
  };

  const openPhotoModal = (photo) => {
    setSelectedPhoto(photo);
    setIsPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setIsPhotoModalOpen(false);
  };

  const getFullSizeImageUrl = (driveUrl) => {
    if (!driveUrl) return '';
    
    const fileIdMatch = driveUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      // Пробуем несколько вариантов URL для полноразмерного изображения
      // Приоритет: googleusercontent > drive.google.com/uc > thumbnail с большим размером
      return `https://lh3.googleusercontent.com/d/${fileId}=w1920-h1080`;
    }
    
    return driveUrl;
  };

  // Comments management
  const loadComments = async () => {
    try {
      const commentsResponse = await axios.get(`${API}/projects/${id}/comments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setComments(commentsResponse.data.comments || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setAddingComment(true);
      
      await axios.post(`${API}/projects/${id}/comments`, {
        comment: newComment.trim()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setNewComment('');
      setShowAddComment(false);
      
      // Reload only comments, not entire project data
      await loadComments();
      
    } catch (err) {
      console.error('Failed to add comment:', err);
      alert('Не удалось добавить комментарий: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setAddingComment(false);
    }
  };

  const addLink = async () => {
    if (!newLink.trim()) return;
    
    try {
      await axios.post(`${API}/projects/${id}/links`, {
        title: linkDescription.trim() || newLink,
        url: newLink.trim(),
        description: linkDescription.trim()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setNewLink('');
      setLinkDescription('');
      setShowAddLink(false);
      loadLinks(); // Reload links
      
    } catch (err) {
      console.error('Failed to add link:', err);
      alert('Не удалось добавить ссылку: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const deleteProject = async () => {
    const confirmMessage = `Вы уверены, что хотите удалить проект "${project.name}"?\n\nЭто действие нельзя отменить. Все данные проекта (комментарии, задачи, фотографии, файлы) будут удалены.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setDeleting(true);
      
      await axios.delete(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Navigate back to projects list
      navigate('/projects-list');
      
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Не удалось удалить проект: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSmartBackNavigation = () => {
    if (referrerInfo && referrerInfo.path) {
      // If came from projects-list with filters, restore them
      if (referrerInfo.path === '/projects-list' && referrerInfo.filters) {
        // Save filters to localStorage for ProjectsList to restore
        localStorage.setItem('projectsListFilters', JSON.stringify(referrerInfo.filters));
      }
      
      // Navigate to referrer page
      navigate(referrerInfo.path);
    } else {
      // Fallback navigation based on browser history
      if (window.history.length > 1) {
        navigate(-1); // Go back in browser history
      } else {
        // Default fallback
        navigate('/projects-list');
      }
    }
  };

  const editContact = (index) => {
    const contact = contacts[index];
    setNewContact({
      name: contact.name,
      position: contact.position || '',
      phone: contact.phone || '',
      email: contact.email || ''
    });
    setEditingContactIndex(index);
    setShowAddContact(true);
  };

  const deleteContact = async (index) => {
    if (!window.confirm('Удалить этот контакт?')) return;
    
    try {
      const updatedContacts = contacts.filter((_, i) => i !== index);
      
      await axios.put(`${API}/projects/${id}`, {
        ...project,
        contacts: updatedContacts
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setContacts(updatedContacts);
      
    } catch (err) {
      console.error('Failed to delete contact:', err);
      alert('Не удалось удалить контакт: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const toggleFlag = async (flagName) => {
    try {
      const currentFlags = project.flags || [];
      const updatedFlags = currentFlags.includes(flagName)
        ? currentFlags.filter(f => f !== flagName)
        : [...currentFlags, flagName];

      await axios.put(`${API}/projects/${id}`, {
        ...project,
        flags: updatedFlags
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      loadProjectData(); // Reload project data
      
    } catch (err) {
      console.error('Failed to update flags:', err);
      alert('Не удалось обновить флаг: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Можно загружать только изображения');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Размер файла не должен превышать 10MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview({
        file: file,
        url: e.target.result,
        name: file.name,
        size: file.size
      });
    };
    reader.readAsDataURL(file);
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      // Use photos/upload endpoint to add to photos section, not comments
      const response = await axios.post(`${API}/projects/${id}/photos/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Show success message instead of error
      alert(`Фото "${file.name}" успешно загружено!`);
      
      // Reload photos
      loadPhotos();
      
      // Clear preview and reset file input
      setPhotoPreview(null);
      event.target.value = '';
      
    } catch (err) {
      console.error('Failed to upload photo:', err);
      alert('Не удалось загрузить фото: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
      // Clear preview on error too
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  // New function for file upload in comments
  const uploadFileToComment = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('Размер файла не должен превышать 50MB');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', `Файл проекта ${project.name}`);
      
      await axios.post(`${API}/projects/${id}/files/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Reload photos and comments
      loadProjectData();
      
      // Reset file input
      event.target.value = '';
      
    } catch (err) {
      console.error('Failed to upload file:', err);
      alert('Не удалось загрузить файл: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setUploading(false);
    }
  };
  
  const deletePhoto = async (photoId) => {
    if (!confirm('Вы уверены, что хотите удалить это фото?')) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Reload photos
      loadProjectData();
      
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Не удалось удалить фото: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const deleteComment = async (commentId) => {
    if (!confirm('Вы уверены, что хотите удалить этот комментарий?')) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      loadProjectData(); // Reload to refresh comments
      
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert('Не удалось удалить комментарий: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  // Check if user can edit this project
  const canEditProject = () => {
    if (!user || !project) return false;
    
    // Admin and lead can edit any project
    if (user.role === 'admin' || user.role === 'lead') return true;
    
    // Manager can edit only assigned projects
    if (user.role === 'manager') {
      return project.assigned_manager_id === user.id;
    }
    
    // Viewer can only view, not edit
    if (user.role === 'viewer') return false;
    
    return false;
  };

  const formatCurrency = (amount, currency = 'KZT') => {
    if (!amount) return 'Не указано';
    
    if (currency === 'KZT') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'USD') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'RUB') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₽';
    } else {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ' + currency;
    }
  };

  // File download function
  const downloadFile = async (fileUrl, fileName) => {
    try {
      // Extract file ID from Google Drive URL
      const fileIdMatch = fileUrl.match(/[-\w]{25,}/);
      if (!fileIdMatch) {
        throw new Error('Invalid file URL');
      }
      
      const fileId = fileIdMatch[0];
      
      // Use our backend endpoint for download
      const response = await fetch(`${API}/files/download/${fileId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      
      // Get the blob content
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('Ошибка при скачивании файла: ' + error.message);
    }
  };

  // Manager assignment functions
  const loadManagers = async () => {
    try {
      const response = await axios.get(`${API}/managers`);
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const handleAssignManager = async (managerId) => {
    try {
      await axios.post(`${API}/projects/${id}/assign`, { manager_id: managerId });
      setAssigningManager(false);
      loadProjectData(); // Reload to see updated assignment
    } catch (error) {
      console.error('Failed to assign manager:', error);
      alert('Ошибка при назначении менеджера: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Load managers when component mounts
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'lead') {
      loadManagers();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ошибка</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/kanban')}
            className="btn-primary"
          >
            Вернуться к проектам
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={handleSmartBackNavigation}
                className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="back-button"
                title={referrerInfo?.path ? `Назад к ${referrerInfo.path}` : 'Назад'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <img 
                    src="/profland-logo-new.jpg" 
                    alt="Profland CRM" 
                    className="w-8 h-8 object-contain rounded-lg"
                  />
                </div>
                <div className="ml-4">
                  <h1 className="text-xl font-semibold text-gray-900">{project.name}</h1>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900" data-testid="project-title">
                  {project.name}
                </h1>
                <p className="text-gray-600 mt-1">
                  {project.city} • {getIndustryLabel(project.industry)}
                </p>
              </div>
              
              {/* Status and Flags */}
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn-primary mr-4"
                  data-testid="edit-project-button"
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Редактировать
                </button>
                
                <span className={`status-badge status-${project.stage.replace('_', '-')}`}>
                  {getStageLabel(project.stage)}
                </span>
                
                {project.flags?.map((flag) => (
                  <span key={flag} className={`status-badge flag-${flag}`}>
                    {flag === 'hot' ? '🔥 Горячий' : flag === 'paused' ? '⏸️ Пауза' : flag === 'waiting_construction' ? '🏗️ Стройготовность' : '⚠️ Риск'}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <nav className="flex space-x-8" aria-label="Tabs">
              {[
                { id: 'overview', name: 'Обзор', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
                { id: 'tasks', name: 'Задачи', icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
                { id: 'contacts', name: 'Контакты', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                  data-testid={`tab-${tab.id}`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            {activeTab === 'overview' && (
              <div data-testid="overview-tab-content" className="space-y-8">
                {/* Main Project Info Card */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Project Summary */}
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-blue-600 mb-1">Описание проекта</h3>
                        <p className="text-gray-800">
                          {project.description || 'Описание проекта не указано'}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Заказчик</h4>
                          <p className="text-gray-900">{project.customer_company || 'Не указан'}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Отрасль</h4>
                          <p className="text-gray-900">{getIndustryLabel(project.industry)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Key Metrics */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="text-sm font-medium text-gray-600 mb-1">Бюджет проекта</h4>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatCurrency(project.budget_amount, project.budget_currency)}
                        </p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h4 className="text-sm font-medium text-gray-600 mb-1">Текущий этап</h4>
                        <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {getStageLabel(project.stage)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Project Details */}
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Детали проекта
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Город поставки</h4>
                          <p className="text-gray-900">{project.city || 'Не указан'}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Фаза готовности</h4>
                          <p className="text-gray-900">
                            {readinessLabels[project.readiness_phase] || project.readiness_phase}
                          </p>
                        </div>
                        
                        {(project.build_start_year || project.build_end_year) && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-600 mb-1">Сроки строительства</h4>
                            <p className="text-gray-900">
                              {project.build_start_year && project.build_end_year
                                ? `${project.build_start_year} - ${project.build_end_year}`
                                : project.build_start_year
                                ? `От ${project.build_start_year}`
                                : `До ${project.build_end_year}`
                              }
                            </p>
                          </div>
                        )}
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Назначенный менеджер</h4>
                          {(user?.role === 'admin' || user?.role === 'lead') ? (
                            assigningManager ? (
                              <div className="flex flex-col space-y-2">
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleAssignManager(e.target.value);
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-3 py-1"
                                  defaultValue=""
                                >
                                  <option value="">Выберите менеджера</option>
                                  {managers.map(manager => (
                                    <option key={manager.id} value={manager.id}>
                                      {manager.name} ({manager.role})
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => setAssigningManager(false)}
                                  className="text-sm text-gray-600 hover:text-gray-800 self-start"
                                >
                                  Отмена
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <p className="text-gray-900">
                                  {project.assigned_manager_name || 'Не назначен'}
                                </p>
                                <button
                                  onClick={() => setAssigningManager(true)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                  title="Изменить менеджера"
                                >
                                  ✏️
                                </button>
                              </div>
                            )
                          ) : (
                            // Manager role - can assign unassigned projects to themselves
                            <div className="flex items-center gap-2">
                              <p className="text-gray-900">
                                {project.assigned_manager_name || 'Не назначен'}
                              </p>
                              {!project.assigned_manager_id && user?.role === 'manager' && (
                                <button
                                  onClick={() => handleAssignManager(user.id)}
                                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                  title="Назначить на себя"
                                >
                                  Взять в работу
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Создан</h4>
                          <p className="text-gray-900">
                            {new Date(project.created_at).toLocaleDateString('ru-RU')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Latest Task */}
                    {tasks && tasks.length > 0 && (
                      <div className="bg-white rounded-xl p-6 border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          Последняя задача
                        </h3>
                        
                        {(() => {
                          const latestTask = tasks.reduce((latest, task) => {
                            const taskDate = new Date(task.created_at || 0);
                            const latestDate = new Date(latest.created_at || 0);
                            return taskDate > latestDate ? task : latest;
                          });
                          
                          return (
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{latestTask.title}</h4>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Дедлайн: {new Date(latestTask.deadline).toLocaleDateString('ru-RU')}
                                  </p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  latestTask.completed 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {latestTask.completed ? 'Выполнено' : 'В работе'}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Right Column - Activity & Media */}
                  <div className="space-y-6">
                    {/* Project Photos */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Фотографии проекта
                        </h3>
                        
                        {canEditProject() && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={uploadPhoto}
                              disabled={uploading}
                              className="hidden"
                              id="photo-upload"
                            />
                            <label
                              htmlFor="photo-upload"
                              className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white ${
                                uploading 
                                  ? 'bg-gray-400 cursor-not-allowed' 
                                  : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                              } transition-colors duration-200`}
                            >
                              {uploading ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Загрузка...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                  Загрузить фото
                                </>
                              )}
                            </label>
                          </div>
                        )}
                      </div>
                      
                      {/* Photo Preview */}
                      {photoPreview && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <img 
                                src={photoPreview.url} 
                                alt={photoPreview.name}
                                className="w-20 h-20 object-cover rounded-lg border border-gray-300"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-blue-900">Готово к загрузке:</p>
                              <p className="text-sm text-blue-700 truncate">{photoPreview.name}</p>
                              <p className="text-xs text-blue-600">
                                Размер: {(photoPreview.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <button
                                onClick={() => {
                                  setPhotoPreview(null);
                                  document.getElementById('photo-upload').value = '';
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {photos && photos.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {photos.map((photo) => (
                            <div key={photo.id} className="relative group">
                              <div className="aspect-w-1 aspect-h-1 bg-gray-200 rounded-lg overflow-hidden cursor-pointer"
                                   onClick={() => openPhotoModal(photo)}>
                                <img
                                  src={convertDriveUrlToViewable(photo.drive_web_link)}
                                  alt={photo.filename}
                                  className="w-full h-48 object-cover group-hover:opacity-75 transition-opacity"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div className="hidden w-full h-full bg-gray-100 items-center justify-center">
                                  <div className="text-center">
                                    <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p className="text-xs text-gray-500">{photo.filename}</p>
                                  </div>
                                </div>
                                
                                {/* Увеличительное стекло */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                  </svg>
                                </div>
                              </div>
                              
                              {canEditProject() && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deletePhoto(photo.id);
                                  }}
                                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                                  title="Удалить фото"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                              
                              <div className="mt-2">
                                <p className="text-xs text-gray-600 truncate">{photo.filename}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(photo.uploaded_at).toLocaleDateString('ru-RU')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="mb-2">Фотографии пока не загружены</p>
                          {canEditProject() && (
                            <p className="text-sm text-blue-600">Используйте кнопку выше для загрузки фотографий</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Project Flags Section */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 2H21l-3 6 3 6h-8.5l-1-2H5a2 2 0 00-2 2zm9-13.5V9" />
                        </svg>
                        Статус проекта
                      </h3>
                      
                      {canEditProject() ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ToggleSwitch
                            label="Горячий проект"
                            icon="🔥"
                            checked={(project.flags || []).includes('hot')}
                            onChange={() => toggleFlag('hot')}
                            color="orange"
                          />
                          <ToggleSwitch
                            label="Проект на паузе"
                            icon="⏸️"
                            checked={(project.flags || []).includes('paused')}
                            onChange={() => toggleFlag('paused')}
                            color="yellow"
                          />
                          <ToggleSwitch
                            label="Проект в зоне риска"
                            icon="⚠️"
                            checked={(project.flags || []).includes('risk')}
                            onChange={() => toggleFlag('risk')}
                            color="red"
                          />
                          <ToggleSwitch
                            label="Ожидаем стройготовность"
                            icon="🏗️"
                            checked={(project.flags || []).includes('waiting_construction')}
                            onChange={() => toggleFlag('waiting_construction')}
                            color="blue"
                          />
                          <ToggleSwitch
                            label="Ждем финансирование"
                            icon="💰"
                            checked={(project.flags || []).includes('waiting_funding')}
                            onChange={() => toggleFlag('waiting_funding')}
                            color="cyan"
                          />
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {project.flags && project.flags.length > 0 ? (
                            project.flags.map((flag) => (
                              <span key={flag} className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                flag === 'hot' ? 'bg-orange-100 text-orange-800' :
                                flag === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                                flag === 'risk' ? 'bg-red-100 text-red-800' :
                                flag === 'waiting_construction' ? 'bg-blue-100 text-blue-800' :
                                flag === 'waiting_funding' ? 'bg-cyan-100 text-cyan-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {flag === 'hot' ? '🔥 Горячий' : 
                                 flag === 'paused' ? '⏸️ На паузе' : 
                                 flag === 'risk' ? '⚠️ В зоне риска' : 
                                 flag === 'waiting_construction' ? '🏗️ Ожидаем стройготовность' :
                                 flag === 'waiting_funding' ? '💰 Ждут финансирования' :
                                 flag}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500">Статус не установлен</span>
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
                
                {/* Full Width Comments Section */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03 8-9 8s9 3.582 9 8z" />
                      </svg>
                      Комментарии и файлы
                    </h3>
                    
                    {canEditProject() && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowAddComment(!showAddComment)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Комментарий
                        </button>
                        
                        <input
                          type="file"
                          accept="*/*"
                          onChange={uploadFileToComment}
                          disabled={uploading}
                          className="hidden"
                          id="file-upload-comment"
                        />
                        <label
                          htmlFor="file-upload-comment"
                          className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors cursor-pointer ${
                            uploading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {uploading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Загрузка...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              Файл
                            </>
                          )}
                        </label>
                        
                        <button
                          onClick={() => setShowAddLink(!showAddLink)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Ссылка
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Add Comment Form */}
                  {showAddComment && canEditProject() && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Добавьте комментарий..."
                        className="w-full p-3 border border-gray-300 rounded-md resize-none"
                        rows="3"
                      />
                      <div className="flex justify-end space-x-2 mt-3">
                        <button
                          onClick={() => {
                            setShowAddComment(false);
                            setNewComment('');
                          }}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Отмена
                        </button>
                        <button
                          onClick={addComment}
                          disabled={!newComment.trim() || addingComment}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {addingComment ? 'Добавление...' : 'Добавить комментарий'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Add Link Form */}
                  {showAddLink && canEditProject() && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <div className="space-y-3">
                        <input
                          type="url"
                          value={newLink}
                          onChange={(e) => setNewLink(e.target.value)}
                          placeholder="https://example.com"
                          className="w-full p-3 border border-gray-300 rounded-md"
                        />
                        <input
                          type="text"
                          value={linkDescription}
                          onChange={(e) => setLinkDescription(e.target.value)}
                          placeholder="Описание ссылки (опционально)"
                          className="w-full p-3 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div className="flex justify-end space-x-2 mt-3">
                        <button
                          onClick={() => {
                            setShowAddLink(false);
                            setNewLink('');
                            setLinkDescription('');
                          }}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Отмена
                        </button>
                        <button
                          onClick={addLink}
                          disabled={!newLink.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Добавить ссылку
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Links Display */}
                  {links && links.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Ссылки ({links.length})
                      </h4>
                      <div className="space-y-2">
                        {links.map((link) => (
                          <div key={link.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <a 
                                  href={link.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 font-medium truncate block"
                                >
                                  {link.title}
                                </a>
                                {link.description && (
                                  <p className="text-sm text-gray-600 mt-1">{link.description}</p>
                                )}
                                <div className="flex items-center text-xs text-gray-500 mt-1">
                                  <span>{link.added_by_name}</span>
                                  <span className="mx-1">•</span>
                                  <span>{new Date(link.created_at).toLocaleDateString('ru-RU')}</span>
                                </div>
                              </div>
                              {canEditProject() && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Удалить ссылку?')) {
                                      try {
                                        await axios.delete(`${API}/projects/${id}/links/${link.id}`, {
                                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                        });
                                        loadLinks();
                                      } catch (err) {
                                        console.error('Failed to delete link:', err);
                                        alert('Не удалось удалить ссылку');
                                      }
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Comments Display */}
                  {comments && comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="border-l-4 border-blue-200 pl-4 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">{comment.author_name}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">
                                {new Date(comment.created_at).toLocaleDateString('ru-RU', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {(comment.author_id === user?.id || user?.role === 'admin' || user?.role === 'lead') && (
                                <button
                                  onClick={() => deleteComment(comment.id)}
                                  className="text-red-500 hover:text-red-700 text-xs"
                                >
                                  Удалить
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {comment.file_url ? (
                            <div className="mt-1">
                              <button
                                onClick={() => downloadFile(comment.file_url, comment.file_name || 'Файл')}
                                className="text-green-600 hover:text-green-800 text-sm flex items-center cursor-pointer bg-transparent border-none p-0"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                </svg>
                                {comment.file_name || 'Файл'}
                              </button>
                              {comment.comment && !comment.comment.startsWith('📎 Загружен файл:') && (
                                <p className="text-sm text-gray-600 mt-1">{comment.comment}</p>
                              )}
                            </div>
                          ) : comment.link_url ? (
                            <div className="mt-1">
                              <a
                                href={comment.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                {comment.link_description || comment.link_url}
                              </a>
                              {comment.comment && comment.comment !== `🔗 [${comment.link_description || 'Ссылка'}](${comment.link_url})` && (
                                <p className="text-sm text-gray-600 mt-1">{comment.comment}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 mt-1">{comment.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : project.notes ? (
                    <div className="space-y-4">
                      <div className="border-l-4 border-blue-200 pl-4 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">Системная заметка</span>
                          <span className="text-xs text-gray-500">
                            {new Date(project.updated_at).toLocaleDateString('ru-RU')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{project.notes}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p className="mb-2">Комментариев и файлов пока нет</p>
                      {canEditProject() && (
                        <p className="text-sm text-blue-600">Используйте кнопки выше для добавления контента</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Edit and Delete Buttons */}
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="btn-primary px-8 py-3 text-lg"
                    data-testid="edit-project-button"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Редактировать проект
                  </button>
                  
                  {(user?.role === 'admin' || user?.role === 'lead') && (
                    <button
                      onClick={deleteProject}
                      disabled={deleting}
                      className="px-8 py-3 text-lg border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="delete-project-button"
                    >
                      {deleting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2 inline-block"></div>
                          Удаление...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Удалить проект
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Actors functionality removed */}

            {activeTab === 'tasks' && (
              <div data-testid="tasks-tab-content">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Задачи по проекту</h3>
                  {canEditProject() && (
                    <div className="text-sm text-gray-500">
                      Только назначенный менеджер может управлять задачами
                    </div>
                  )}
                </div>

                {/* Add new task form */}
                {canEditProject() && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Добавить новую задачу</h4>
                    
                    {/* Template selector */}
                    {taskTemplates && taskTemplates.length > 0 && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Выбрать из шаблонов:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              setNewTask({...newTask, title: e.target.value});
                              e.target.value = ''; // Reset select
                            }
                          }}
                          className="form-select w-full text-sm"
                        >
                          <option value="">Выбрать шаблон задачи...</option>
                          {taskTemplates.map((template, index) => (
                            <option key={index} value={template}>{template}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Название задачи:
                        </label>
                        <input
                          type="text"
                          placeholder="Введите название задачи или выберите из шаблонов выше"
                          value={newTask.title}
                          onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Крайний срок:
                          </label>
                          <input
                            type="date"
                            value={newTask.deadline}
                            onChange={(e) => setNewTask({...newTask, deadline: e.target.value})}
                            className="form-input w-full"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={addTask}
                            disabled={!newTask.title.trim()}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed h-fit"
                          >
                            Добавить
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.map((task) => {
                      const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !task.completed;
                      const isDueSoon = task.deadline && !task.completed && 
                        Math.ceil((new Date(task.deadline) - new Date()) / (1000 * 60 * 60 * 24)) <= 3;

                      return (
                        <div key={task.id} className={`p-4 rounded-lg border transition-all ${
                          task.completed ? 'bg-green-50 border-green-200' : 
                          isOverdue ? 'bg-red-50 border-red-300' :
                          isDueSoon ? 'bg-yellow-50 border-yellow-300' :
                          'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {editingTaskId === task.id ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    defaultValue={task.title}
                                    className="form-input w-full"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        updateTask(task.id, { title: e.target.value });
                                      }
                                    }}
                                    onBlur={(e) => updateTask(task.id, { title: e.target.value })}
                                    autoFocus
                                  />
                                  <input
                                    type="date"
                                    defaultValue={task.deadline}
                                    className="form-input"
                                    onChange={(e) => updateTask(task.id, { deadline: e.target.value })}
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={task.completed}
                                      onChange={(e) => updateTask(task.id, { completed: e.target.checked })}
                                      disabled={!canEditProject()}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <h4 className={`font-medium ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                      {task.title}
                                    </h4>
                                    {isOverdue && (
                                      <span className="text-red-600 text-xs font-medium bg-red-100 px-2 py-1 rounded">
                                        Просрочено
                                      </span>
                                    )}
                                    {isDueSoon && !isOverdue && (
                                      <span className="text-yellow-600 text-xs font-medium bg-yellow-100 px-2 py-1 rounded">
                                        Скоро дедлайн
                                      </span>
                                    )}
                                  </div>
                                  {task.deadline && (
                                    <p className="text-xs text-gray-500 mt-1 ml-6">
                                      Срок: {new Date(task.deadline).toLocaleDateString('ru-RU')}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1 ml-6">
                                    Создана: {new Date(task.created_at).toLocaleDateString('ru-RU')}
                                  </p>
                                </>
                              )}
                            </div>
                            
                            {canEditProject() && (
                              <div className="ml-4 flex gap-2">
                                <button
                                  onClick={() => setEditingTaskId(editingTaskId === task.id ? null : task.id)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                  title="Редактировать"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteTask(task.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                  title="Удалить"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h5.586a1 1 0 00.707-.293l5.414-5.414a1 1 0 00.293-.707V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <p>Задач пока не добавлено</p>
                    {canEditProject() && (
                      <p className="text-sm mt-1">Добавьте первую задачу используя форму выше</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div data-testid="contacts-tab-content">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Контакты заказчика</h3>
                  {canEditProject() && (
                    <div className="text-sm text-gray-500">
                      Только назначенный менеджер может управлять контактами
                    </div>
                  )}
                </div>

                {/* Add new contact form */}
                {canEditProject() && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <input
                        type="text"
                        placeholder="Имя и фамилия"
                        value={newContact.name}
                        onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Должность"
                        value={newContact.position}
                        onChange={(e) => setNewContact({...newContact, position: e.target.value})}
                        className="form-input"
                      />
                      <input
                        type="tel"
                        placeholder="Телефон"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                        className="form-input"
                      />
                      <div className="flex gap-2">
                        <input
                          type="email"
                          placeholder="Email"
                          value={newContact.email}
                          onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                          className="form-input flex-1"
                        />
                        <button
                          onClick={addContact}
                          disabled={!newContact.name.trim()}
                          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Добавить
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {contacts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {contacts.map((contact, index) => (
                      <div key={index} className="bg-white rounded-lg border p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{contact.name}</h4>
                            {contact.position && (
                              <p className="text-sm text-gray-600 mt-1">{contact.position}</p>
                            )}
                            <div className="mt-3 space-y-2">
                              {contact.phone && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                                    {contact.phone}
                                  </a>
                                </div>
                              )}
                              {contact.email && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                  </svg>
                                  <a href={`mailto:${contact.email}`} className="hover:text-blue-600">
                                    {contact.email}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Contact Actions */}
                          {canEditProject() && (
                            <div className="flex flex-col space-y-1 ml-4">
                              <button
                                onClick={() => editContact(index)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                title="Редактировать контакт"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => deleteContact(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                                title="Удалить контакт"
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p>Контактов заказчика пока не добавлено</p>
                    {canEditProject() && (
                      <p className="text-sm mt-1">Добавьте первый контакт используя форму выше</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {showEditModal && (
        <ProjectEditModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {/* Photo Modal */}
      {isPhotoModalOpen && selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
             onClick={closePhotoModal}>
          <div className="relative w-full h-full max-w-7xl max-h-full flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button
              onClick={closePhotoModal}
              className="absolute top-4 right-4 bg-black bg-opacity-70 text-white rounded-full p-3 hover:bg-opacity-90 transition-all z-10 shadow-lg"
              title="Закрыть"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Photo Container */}
            <div className="flex-1 flex items-center justify-center p-4 pb-24">
              <img
                src={getFullSizeImageUrl(selectedPhoto.drive_web_link)}
                alt={selectedPhoto.filename}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                onError={(e) => {
                  // Fallback to alternative URLs if full size fails
                  const fileIdMatch = selectedPhoto.drive_web_link.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                  if (fileIdMatch) {
                    const fileId = fileIdMatch[1];
                    if (e.target.src.includes('lh3.googleusercontent.com')) {
                      e.target.src = `https://drive.google.com/uc?export=view&id=${fileId}`;
                    } else if (e.target.src.includes('drive.google.com/uc')) {
                      e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920-h1080`;
                    } else {
                      e.target.src = convertDriveUrlToViewable(selectedPhoto.drive_web_link);
                    }
                  }
                }}
              />
            </div>

            {/* Photo Info Bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent text-white p-6">
              <div className="max-w-4xl mx-auto">
                <h3 className="font-semibold text-xl mb-2">{selectedPhoto.filename}</h3>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
                  <span>Загружено: {new Date(selectedPhoto.uploaded_at).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                  
                  {selectedPhoto.file_size && (
                    <span>Размер: {(selectedPhoto.file_size / 1024).toFixed(1)} KB</span>
                  )}
                  
                  <span>•</span>
                  
                  <a
                    href={selectedPhoto.drive_web_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-200 transition-colors flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                    </svg>
                    Открыть в Google Drive
                  </a>
                </div>
                
                {selectedPhoto.description && (
                  <p className="text-gray-300 mt-2">{selectedPhoto.description}</p>
                )}
              </div>
            </div>

            {/* Navigation arrows (if needed in future) */}
            <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
              <div className="text-white/70 text-sm">ESC для закрытия</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;